import { useEffect, useMemo, useState } from "react";
import { listChildren, listReportsForMonth, upsertDraftReport, getRenderedPdfDownloadUrl, resetReportToDraft } from "../lib/firestore";
import { getAccessToken, getCachedToken } from "../lib/auth";
import { pickPhotos } from "../lib/picker";
import { generateDraft } from "../lib/draftApi";
import { renderReport } from "../lib/renderApi";
import type { Child, Report } from "../types";
import { monthKey } from "../types";
import { ChildRow } from "./ChildRow";

type Props = { uid: string; displayName: string };

function shiftMonth(period: string, delta: 1 | -1): string {
  const [y, m] = period.split("-").map(Number) as [number, number];
  const next = new Date(Date.UTC(y, m - 1 + delta, 1));
  return monthKey(next);
}

function periodLabel(period: string): string {
  const [y, m] = period.split("-").map(Number) as [number, number];
  const date = new Date(Date.UTC(y, m - 1, 1));
  return date.toLocaleString("en-AU", { month: "long", year: "numeric", timeZone: "UTC" });
}

export function MonthBoard({ uid, displayName }: Props) {
  const [period, setPeriod] = useState<string>(() => monthKey(new Date()));
  const [children, setChildren] = useState<Child[]>([]);
  const [reports, setReports] = useState<Map<string, Report>>(new Map());
  // Pre-fetched PDF download URLs (one Storage SDK call per rendered
  // report at refresh time — avoids each ChildRow firing its own).
  const [pdfUrls, setPdfUrls] = useState<Map<string, string>>(new Map());
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = async () => {
    const [kids, reps] = await Promise.all([
      listChildren(uid),
      listReportsForMonth(uid, period),
    ]);
    const map = new Map<string, Report>();
    for (const r of reps) map.set(r.child_id, r);
    setChildren(kids.filter((c) => c.ended_on === null));
    setReports(map);

    // Resolve URLs for any rendered reports in parallel.
    const urlEntries = await Promise.all(
      reps
        .filter((r) => r.status === "rendered" && r.pdf_storage_path)
        .map(async (r) => {
          try {
            const url = await getRenderedPdfDownloadUrl(r.pdf_storage_path!);
            return [r.id, url] as const;
          } catch (err) {
            console.error(`PDF URL fetch failed for ${r.id}:`, err);
            return null;
          }
        })
    );
    setPdfUrls(new Map(urlEntries.filter((e): e is readonly [string, string] => e !== null)));
  };

  useEffect(() => {
    refresh();
  }, [uid, period]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePickPhotos = async (childId: string) => {
    // Only open the about:blank popup when we already have a token —
    // otherwise the Firebase signIn popup would race against this
    // about:blank popup and Vera ends up with two windows. When the
    // token cache is cold we let signIn() pop its window first, then
    // pickPhotos falls back to opening the picker in this tab (the
    // sign-in popup will close after auth, and the picker URL opens
    // here). Caller hits the cold path at most once per session.
    const cached = getCachedToken();
    const popup = cached ? window.open("about:blank", "_blank") : null;
    setBusy(childId);
    try {
      const accessToken = cached ?? (await getAccessToken());
      const items = await pickPhotos(accessToken, popup);
      if (items.length !== 2) {
        alert(
          `Please pick exactly 2 photos (you picked ${items.length}). The first drives the story/analysis/outcomes; the second drives what-next/evaluation.`
        );
        return;
      }
      const photos = items.map((it, i) => ({
        gp_media_id: it.id,
        gp_thumbnail_url: it.mediaFile.baseUrl,
        is_main: i === 0,
        taken_at: it.createTime ? it.createTime.slice(0, 10) : null,
      }));
      await upsertDraftReport(uid, childId, period, displayName, photos);
      await refresh();
    } catch (err) {
      console.error(err);
      if (popup && !popup.closed) popup.close();
      alert(`Photo pick failed: ${err instanceof Error ? err.message : "unknown"}`);
    } finally {
      setBusy(null);
    }
  };

  // Generate is enabled whenever a draft-status report has photos.
  // Existing draft content gets overwritten — that's the point of
  // "regenerate". To preserve content, Vera approves before regenerating;
  // /draft refuses to overwrite an approved/rendered report.
  const handleRegenerate = async (reportId: string) => {
    if (!confirm("Regenerate the AI draft? This will overwrite the current content and (if approved/rendered) reset the report back to draft.")) return;
    setBusy(reportId);
    try {
      const accessToken = getCachedToken() ?? (await getAccessToken());
      await resetReportToDraft(uid, reportId);
      await generateDraft({ reportId, pickerAccessToken: accessToken });
      await refresh();
    } catch (err) {
      console.error(err);
      alert(`Regenerate failed: ${err instanceof Error ? err.message : "unknown"}`);
    } finally {
      setBusy(null);
    }
  };

  const draftableReports = useMemo(
    () =>
      Array.from(reports.values()).filter(
        (r) => r.status === "draft" && r.photos.length > 0
      ),
    [reports]
  );
  const generateDisabled = draftableReports.length === 0 || busy !== null;

  const handleGenerateAll = async () => {
    setBusy("generating");
    try {
      const accessToken = await getAccessToken();
      const results = await Promise.all(
        draftableReports.map(async (r) => {
          try {
            await generateDraft({ reportId: r.id, pickerAccessToken: accessToken });
            return { id: r.id, ok: true as const };
          } catch (err) {
            console.error(`Draft failed for ${r.id}:`, err);
            return { id: r.id, ok: false as const };
          }
        })
      );
      const failed = results.filter((r) => !r.ok).length;
      await refresh();
      if (failed > 0) {
        alert(`Drafted ${results.length - failed} of ${results.length}. ${failed} failed (see console).`);
      }
    } catch (err) {
      console.error(err);
      alert(`Generate failed: ${err instanceof Error ? err.message : "unknown"}`);
    } finally {
      setBusy(null);
    }
  };

  const renderableReports = useMemo(
    () => Array.from(reports.values()).filter((r) => r.status === "approved"),
    [reports]
  );
  const renderDisabled = renderableReports.length === 0 || busy !== null;

  const handleRenderAll = async () => {
    setBusy("rendering");
    try {
      const accessToken = await getAccessToken();
      const results = await Promise.all(
        renderableReports.map(async (r) => {
          try {
            await renderReport({ reportId: r.id, pickerAccessToken: accessToken });
            return { id: r.id, ok: true as const };
          } catch (err) {
            console.error(`Render failed for ${r.id}:`, err);
            return { id: r.id, ok: false as const };
          }
        })
      );
      const failed = results.filter((r) => !r.ok).length;
      await refresh();
      if (failed > 0) {
        alert(`Rendered ${results.length - failed} of ${results.length}. ${failed} failed (see console).`);
      }
    } catch (err) {
      console.error(err);
      alert(`Render failed: ${err instanceof Error ? err.message : "unknown"}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="p-4 max-w-2xl mx-auto">
      <header className="flex items-center justify-between mb-4">
        <button
          onClick={() => setPeriod(shiftMonth(period, -1))}
          aria-label="Previous month"
          className="text-[#ff6b35] font-medium px-2"
        >
          ◀
        </button>
        <h2 data-testid="month-label" className="text-lg font-semibold">
          {periodLabel(period)}
        </h2>
        <button
          onClick={() => setPeriod(shiftMonth(period, 1))}
          aria-label="Next month"
          className="text-[#ff6b35] font-medium px-2"
        >
          ▶
        </button>
      </header>

      <ul className="border rounded">
        {children.length === 0 && (
          <li className="p-3 text-gray-500">No active children — add some in the Roster.</li>
        )}
        {children.map((c) => {
          const r = reports.get(c.id) ?? null;
          return (
            <ChildRow
              key={c.id}
              child={c}
              report={r}
              pdfUrl={r ? pdfUrls.get(r.id) ?? null : null}
              onPickPhotos={handlePickPhotos}
              onRegenerate={handleRegenerate}
            />
          );
        })}
      </ul>

      <div className="mt-6 flex justify-center gap-3 flex-wrap">
        <button
          onClick={handleGenerateAll}
          disabled={generateDisabled}
          className="bg-[#ff6b35] text-white rounded-full px-6 py-3 font-medium disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed"
        >
          {busy === "generating"
            ? `Drafting ${draftableReports.length} reports…`
            : draftableReports.length > 0
            ? `Generate ${draftableReports.length} draft${draftableReports.length === 1 ? "" : "s"}`
            : "No drafts to generate"}
        </button>
        <button
          onClick={handleRenderAll}
          disabled={renderDisabled}
          className="bg-[#ff6b35] text-white rounded-full px-6 py-3 font-medium disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed"
        >
          {busy === "rendering"
            ? `Rendering ${renderableReports.length} PDFs…`
            : renderableReports.length > 0
            ? `Render ${renderableReports.length} PDF${renderableReports.length === 1 ? "" : "s"}`
            : "No PDFs to render"}
        </button>
      </div>
    </div>
  );
}
